import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import SimpleButton from '../common/SimpleButton'
import LoadingSpinner from '../common/LoadingSpinner'
import VideoModal from '../common/VideoModal'
import TooltipedIcon from '../common/TooltipedIcon'
import { uniqBy } from '../../utils/filters/filters'
import { DownloadModernIcon, VideoPlayIcon, InformationIcon } from '../../icons'
import { getVideosList } from '../../utils/requests/videosAPI'
import { downloadFirmware } from '../../utils/requests/firmwareAPI'
import { getFileIcon, replaceExtension } from '../../utils/functions'
import { useFirmwareSteps } from '../../hooks/useFirmwareSteps'
import './firmware-latest-version.scss'

const LatestVersion = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { selectedDevice } = props
  const [videos, setVideos] = useState([])
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [instructionSoftwares, setInstructionSoftwares] = useState([])
  const [uiSoftwares, setUiSoftwares] = useState([])
  const [controlSoftwares, setControlSoftwares] = useState([])
  const [uiControlSoftwares, setUiControlSoftwares] = useState([])
  const { renderModelSteps } = useFirmwareSteps()

  useEffect(() => {
    let files = []

    selectedDevice.versions.forEach((version) => {
      files.push(
        version.files.map((file) => {
          return {
            ...file,
            version_id: version.id,
          }
        }),
      )
    })

    const versions = uniqBy(selectedDevice.versions, 'id')

    const mappedVersionsWithFiles = versions.map((version) => {
      version.files = []

      files = [].concat(...files)
      files.forEach((file) => {
        if (file.version_id === version.id) {
          version.files.push(file)
        }
      })
      return version
    })

    //A.S: Code above is to fix legacy issues where version ids were not unique everytime
    const latestVersionId = selectedDevice.versions[0]?.id

    const activeFiles = mappedVersionsWithFiles
      .flatMap((version) =>
        version.files.map((file) => ({
          ...file,
          isNew: version.id === latestVersionId,
        })),
      )
      .filter((file) => file.isActive)

    const instructionSoftwares = activeFiles.filter((file) => file.software_type === 'Instruction')
    const uiSoftwares = activeFiles.filter((file) => file.software_type === 'UI')
    const controlSoftwares = activeFiles.filter((file) => file.software_type === 'Control')
    const uiControlSoftwares = activeFiles.filter((file) => file.software_type === 'UI+Control')

    setInstructionSoftwares(instructionSoftwares)
    setUiSoftwares(uiSoftwares)
    setControlSoftwares(controlSoftwares)
    setUiControlSoftwares(uiControlSoftwares)
  }, [selectedDevice])

  useEffect(() => {
    setShowSpinner(true)

    const getVideos = async () => {
      const queryParams = {
        model: selectedDevice.model,
        category: 'Firmware Download',
        page: 1,
        limit: 3,
      }

      const { data } = await getVideosList(queryParams)

      setVideos(data || [])
      setShowSpinner(false)
    }

    getVideos()
  }, [selectedDevice])

  const downloadFile = (file) => {
    const body = {
      filename: file.filename || file.label,
      reference_id: file.reference_id,
      prefix: selectedDevice.prefix,
      prefix_number: selectedDevice.prefix_number,
      email: props.authedUser.email,
      country: props.authedUser.country,
      type: file?.software_type === 'Instruction' ? 'document' : 'firmware',
    }

    downloadFirmware(file.id, file.filename, file.filepath, body)
  }

  const openVideoModal = (item) => {
    setSelectedVideo(item)
  }

  const closeVideoModal = () => {
    setSelectedVideo(null)
  }

  const handleNavigateToVideos = () => {
    history.push({
      pathname: `/videos`,
      state: {
        model: videos.length !== 0 ? selectedDevice.model : null,
        withBackNavigation: true,
      },
    })
  }

  const hideSoftwares = selectedDevice?.model?.toLowerCase().includes('hydrim 112')

  const renderFirmwareSection = (softwares, titleKey) => {
    if (softwares.length > 0) {
      return (
        <>
          <div className="inner-separator" />
          <div className="section-title">{t(`firmware_context.${titleKey}`)}</div>
          <div className="firmware-executables-wrapper">
            {softwares.map((file, index) => (
              <div className="firmware-executable" key={index}>
                <button className="download-document" onClick={() => downloadFile(file)} disabled={showSpinner}>
                  <DownloadModernIcon className="download-icon" />
                </button>
                {getFileIcon(file.filename)}
                <div className="file-name">{replaceExtension(file.label || file.filename)}</div>
                {false && file.isNew && <TooltipedIcon icon={<div className="red-dot" />} tooltipText={t('firmware_context.new_release')} />}
              </div>
            ))}
          </div>
        </>
      )
    }
    return null
  }

  return (
    <div className="firmware-latest-version-wrapper">
      <div className="instruction-box-text">
        <div className="icon-wrapper">
          <InformationIcon />
        </div>
        <div className="instruction-wrapper">
          <div className="title">{t('firmware_context.update_instructions')}</div>
          {renderModelSteps(selectedDevice.model)}
        </div>
      </div>
      <div className="firmware-videos-wrapper">
        <div className="section-title">{t('nav.videos')}</div>
        <div className="videos-wrapper">
          {videos.map((video, index) => (
            <div key={index} className="video-item-wrapper" onClick={() => openVideoModal(video)}>
              <div className="thumbnail-wrapper">
                <div className="thumbnail-icon">
                  <VideoPlayIcon />
                </div>
                <img src={video.thumbnail_url} alt="video" />
              </div>
              <p className="title">{video.title}</p>
            </div>
          ))}
          {videos.length === 0 && (
            <div className="no-videos-wrapper">
              <p className="disclaimer">{t('video_context.no_videos_yet')}</p>
            </div>
          )}
        </div>
        <SimpleButton className="action-button" onClick={() => handleNavigateToVideos()} disabled={videos.length === 0}>
          {t('video_context.view_all_videos')}
        </SimpleButton>
      </div>
      {instructionSoftwares.length > 0 && (
        <>
          <div className="inner-separator" />
          <div className="section-title">{t('firmware_context.instructions_other_documents')}</div>
          <div className="firmware-documents-wrapper">
            {instructionSoftwares.map((file, index) => (
              <div className="firmware-documents d-flex" key={index}>
                <div className="name-wrapper d-flex flex-align-center">
                  {getFileIcon(file.label)}
                  <div className="file-name">{replaceExtension(file.label)}</div>
                  {false && file.isNew && <TooltipedIcon icon={<div className="red-dot" />} tooltipText={t('firmware_context.new_release')} />}
                </div>
                <SimpleButton className="action-button" onClick={() => downloadFile(file)} disabled={showSpinner}>
                  {t('download')}
                </SimpleButton>
              </div>
            ))}
          </div>
        </>
      )}

      {!hideSoftwares && renderFirmwareSection(uiSoftwares, 'lcd_board_software')}
      {!hideSoftwares && renderFirmwareSection(controlSoftwares, 'main_board_software')}
      {renderFirmwareSection(uiControlSoftwares, 'lcd_main_board_software')}

      {selectedVideo && <VideoModal title={selectedVideo.title} videoId={selectedVideo.video_id} onCloseVideo={closeVideoModal} />}
      {showSpinner && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function mapStateToProps({ authedUser, userPermissions }) {
  return {
    authedUser,
    userPermissions,
  }
}
export default connect(mapStateToProps)(LatestVersion)

